import { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@awesome.me/kit-70c1f0055f/icons/classic/regular";
import {
  apiImageUrlNeedsSessionFetch,
  toProxyUrl,
} from "@/utils/imageUrl";
import {
  acquireSharedImageBlobUrl,
  releaseSharedImageBlobUrl,
} from "@/services/api/sharedImageBlobCache";

/**
 * Loads a branding logo for watermark display. Opacity is applied inline so admins
 * can configure 0–100% without relying on fixed Tailwind opacity classes.
 */
export function BrandingWatermarkImage({
  logoUrl,
  opacity = 100,
  className,
  imageClassName = "block h-24 w-auto max-w-[280px] object-contain",
  testId,
}: {
  logoUrl: string;
  opacity?: number;
  className?: string;
  imageClassName?: string;
  testId?: string;
}) {
  const [src, setSrc] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    let proxiedForRelease: string | null = null;
    setSrc(null);

    async function load() {
      const proxied = toProxyUrl(logoUrl);
      if (!proxied) return;

      if (apiImageUrlNeedsSessionFetch(proxied)) {
        proxiedForRelease = proxied;
        const resolved = await acquireSharedImageBlobUrl(proxied);
        if (cancelled) return;
        if (resolved) {
          setSrc(resolved);
          return;
        }
      }

      if (!cancelled) {
        setSrc(proxied);
      }
    }

    void load();

    return () => {
      cancelled = true;
      if (proxiedForRelease) {
        releaseSharedImageBlobUrl(proxiedForRelease);
      }
    };
  }, [logoUrl]);

  if (!src) {
    return (
      <div
        className={`flex items-center justify-center ${className ?? ""}`}
        data-testid={testId ? `${testId}-loading` : undefined}
      >
        <FontAwesomeIcon icon={faSpinner} className="h-5 w-5 animate-spin text-white/40" />
      </div>
    );
  }

  return (
    <div className={className}>
      <img
        src={src}
        alt=""
        aria-hidden
        className={imageClassName}
        style={{ opacity: opacity / 100 }}
        onError={() => {
          const proxied = toProxyUrl(logoUrl);
          if (proxied && src !== proxied) {
            setSrc(proxied);
          }
        }}
        data-testid={testId}
      />
    </div>
  );
}
